/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  NgModule,
  ViewEncapsulation,
  Inject,
  Optional,
} from '@angular/core';
import {ENTER, SPACE} from '../keyboard/keycodes';
import {coerceBooleanProperty} from '../coercion/boolean-property';
import {MATERIAL_COMPATIBILITY_MODE} from '../../core/compatibility/compatibility';
import {MdOptgroup} from './optgroup';
/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 0;
/**
 * Event object emitted by MdOption when selected or deselected.
 */
export class MdOptionSelectionChange {
/**
 * @param {?} source
 * @param {?=} isUserInput
 */
constructor(public source: MdOption,
public isUserInput = false) { }
}

function MdOptionSelectionChange_tsickle_Closure_declarations() {
/** @type {?} */
MdOptionSelectionChange.prototype.source;
/** @type {?} */
MdOptionSelectionChange.prototype.isUserInput;
}

/**
 * Single option inside of a `<md-select>` element.
 */
export class MdOption {
private _selected: boolean = false;
private _active: boolean = false;
/**
 * Whether the option is disabled.
 */
private _disabled: boolean = false;
private _id: string = `md-option-${_uniqueIdCounter++}`;
/**
 * Whether the wrapping component is in multiple selection mode.
 */
multiple: boolean = false;
/**
 * The unique ID of the option.
 * @return {?}
 */
get id() { return this._id; }
/**
 * Whether or not the option is currently selected.
 * @return {?}
 */
get selected(): boolean { return this._selected; }
/**
 * The form value of the option.
 */
value: any;
/**
 * Whether the option is disabled.
 * @return {?}
 */
get disabled() { return (this.group && this.group.disabled) || this._disabled; }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) { this._disabled = coerceBooleanProperty(value); }
/**
 * Event emitted when the option is selected or deselected.
 */
onSelectionChange = new EventEmitter<MdOptionSelectionChange>();
/**
 * @param {?} _element
 * @param {?} group
 * @param {?} _isCompatibilityMode
 */
constructor(
private _element: ElementRef,
public readonly group: MdOptgroup,
public _isCompatibilityMode: boolean) {}
/**
 * Whether or not the option is currently active and ready to be selected.
 * An active option displays styles as if it is focused, but the
 * focus is actually retained somewhere else. This comes in handy
 * for components like autocomplete where focus must remain on the input.
 * @return {?}
 */
get active(): boolean {
    return this._active;
  }
/**
 * The displayed value of the option. It is necessary to show the selected option in the
 * select's trigger.
 * @return {?}
 */
get viewValue(): string {
    // TODO(kara): Add input property alternative for node envs.
    return this._getHostElement().textContent.trim();
  }
/**
 * Selects the option.
 * @return {?}
 */
select(): void {
    this._selected = true;
    this._emitSelectionChangeEvent();
  }
/**
 * Deselects the option.
 * @return {?}
 */
deselect(): void {
    this._selected = false;
    this._emitSelectionChangeEvent();
  }
/**
 * Sets focus onto this option.
 * @return {?}
 */
focus(): void {
    this._getHostElement().focus();
  }
/**
 * This method sets display styles on the option to make it appear
 * active. This is used by the ActiveDescendantKeyManager so key
 * events will display the proper options as active on arrow key events.
 * @return {?}
 */
setActiveStyles(): void {
    this._active = true;
  }
/**
 * This method removes display styles on the option that made it appear
 * active. This is used by the ActiveDescendantKeyManager so key
 * events will display the proper options as active on arrow key events.
 * @return {?}
 */
setInactiveStyles(): void {
    this._active = false;
  }
/**
 * Ensures the option is selected when activated from the keyboard.
 * @param {?} event
 * @return {?}
 */
_handleKeydown(event: KeyboardEvent): void {
    if (event.keyCode === ENTER || event.keyCode === SPACE) {
      this._selectViaInteraction();
    }
  }
/**
 * Selects the option while indicating the selection came from the user. Used to
 * determine if the select's view -> model callback should be invoked.
 * @return {?}
 */
_selectViaInteraction(): void {
    if (!this.disabled) {
      this._selected = this.multiple ? !this._selected : true;
      this._emitSelectionChangeEvent(true);
    }
  }
/**
 * Returns the correct tabindex for the option depending on disabled state.
 * @return {?}
 */
_getTabIndex(): string {
    return this.disabled ? '-1' : '0';
  }
/**
 * Fetches the host DOM element.
 * @return {?}
 */
_getHostElement(): HTMLElement {
    return this._element.nativeElement;
  }
/**
 * Emits the selection change event.
 * @param {?=} isUserInput
 * @return {?}
 */
private _emitSelectionChangeEvent(isUserInput = false): void {
    this.onSelectionChange.emit(new MdOptionSelectionChange(this, isUserInput));
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-option, mat-option',
  host: {
    'role': 'option',
    '[attr.tabindex]': '_getTabIndex()',
    '[class.mat-selected]': 'selected',
    '[class.mat-option-multiple]': 'multiple',
    '[class.mat-active]': 'active',
    '[id]': 'id',
    '[attr.aria-selected]': 'selected.toString()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[class.mat-option-disabled]': 'disabled',
    '(click)': '_selectViaInteraction()',
    '(keydown)': '_handleKeydown($event)',
    'class': 'mat-option',
  },
  templateUrl: 'option.html',
  encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: MdOptgroup, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MATERIAL_COMPATIBILITY_MODE, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'value': [{ type: Input },],
'disabled': [{ type: Input },],
'onSelectionChange': [{ type: Output },],
};
}

function MdOption_tsickle_Closure_declarations() {
/** @type {?} */
MdOption.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdOption.ctorParameters;
/** @type {?} */
MdOption.propDecorators;
/** @type {?} */
MdOption.prototype._selected;
/** @type {?} */
MdOption.prototype._active;
/**
 * Whether the option is disabled.
 * @type {?}
 */
MdOption.prototype._disabled;
/** @type {?} */
MdOption.prototype._id;
/**
 * Whether the wrapping component is in multiple selection mode.
 * @type {?}
 */
MdOption.prototype.multiple;
/**
 * The form value of the option.
 * @type {?}
 */
MdOption.prototype.value;
/**
 * Event emitted when the option is selected or deselected.
 * @type {?}
 */
MdOption.prototype.onSelectionChange;
/** @type {?} */
MdOption.prototype._element;
/** @type {?} */
MdOption.prototype.group;
/** @type {?} */
MdOption.prototype._isCompatibilityMode;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
